
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { usePermissions } from "@/hooks/use-permissions";

interface DashboardStats {
  userCount: number;
  insightCount: number;
  pollCount: number;
  activeCountriesCount: number;
}

export const useDashboardStats = (countryCode?: string | null) => {
  const { isSuperAdmin, getAssignedCountries } = usePermissions();
  
  return useQuery({
    queryKey: ['dashboard-stats', countryCode],
    queryFn: async (): Promise<DashboardStats> => {
      // Determine which countries to include in stats
      let targetCountries: string[] = [];
      
      if (isSuperAdmin()) {
        // Super admins see global stats or specific country
        if (countryCode && countryCode !== "global") {
          targetCountries = [countryCode];
        }
        // For global view, no country filter (empty array means all)
      } else {
        // Country admins see only their assigned countries
        const assignedCountries = getAssignedCountries();
        if (countryCode && countryCode !== "global" && assignedCountries.includes(countryCode)) {
          // If specific country requested and admin has access
          targetCountries = [countryCode];
        } else {
          // For global view or no specific country, use all assigned countries
          targetCountries = assignedCountries;
        }
      }

      let userQuery = supabase
        .from('profiles')
        .select('*', { count: 'exact', head: true });
        
      let insightQuery = supabase
        .from('insight_cards')
        .select('*', { count: 'exact', head: true })
        .eq('status', 'published');
        
      let pollQuery = supabase
        .from('polls')
        .select('*', { count: 'exact', head: true })
        .in('status', ['live', 'closed']);

      // Apply country filter if specific countries are targeted
      if (targetCountries.length > 0) {
        userQuery = userQuery.in('country_code', targetCountries);
        insightQuery = insightQuery.in('country_code', targetCountries);
        pollQuery = pollQuery.in('country_code', targetCountries);
      } else if (!isSuperAdmin()) {
        // Country admin with no assigned countries - return zeros
        return {
          userCount: 0,
          insightCount: 0,
          pollCount: 0,
          activeCountriesCount: 0,
        };
      }

      // For active countries count, if admin has assigned countries, show only those
      let countryQuery = supabase
        .from('countries')
        .select('*', { count: 'exact', head: true })
        .eq('active', true);
      
      if (!isSuperAdmin() && targetCountries.length > 0) {
        countryQuery = countryQuery.in('code', targetCountries);
      }

      // Execute all queries
      const [
        { count: userCount, error: userError },
        { count: insightCount, error: insightError },
        { count: pollCount, error: pollError },
        { count: activeCountriesCount, error: countryError }
      ] = await Promise.all([
        userQuery,
        insightQuery,
        pollQuery,
        countryQuery
      ]);

      if (userError) throw userError;
      if (insightError) throw insightError;
      if (pollError) throw pollError;
      if (countryError) throw countryError;

      return {
        userCount: userCount || 0,
        insightCount: insightCount || 0,
        pollCount: pollCount || 0,
        activeCountriesCount: activeCountriesCount || 0,
      };
    }
  });
};
